import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  input,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  signal,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ConfirmationComponent } from '@arc/aureus';
import {
  AddPaymentMethodForm,
  buildAddPaymentForm,
  getPaymentMethodFormDetails,
  PaymentMethodFormComponent,
  PaymentMethodType,
} from '@arc/unified-payments-frontend/shared-payment-accounts';
import { TranslateService } from '@ngx-translate/core';
import {
  AccountPaymentMethod,
  AddPaymentMethodFormSubmitEvent,
  AllowableCardTypes,
  PaymentAccountsPushNotificationMetadata,
  PaymentExclusionOverrideSetting,
  PaymentMethodTypeToggledEvent,
} from '../../models';
import { PadAgreementFormSubmitEvent } from '../../models/forms';
import { PushNotificationsService } from '../../services';
import { WalletSessionServiceFactory } from '../../factories/wallet-session-service.factory';
import { Subscription } from 'rxjs';

@Component({
  selector: 'payment-add-payment-method',
  templateUrl: './add-payment-method.component.html',
  styleUrls: ['./add-payment-method.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddPaymentMethodComponent implements OnInit, OnChanges, OnDestroy {
  @Input() acceptAch: boolean;
  @Input() acceptCreditCard: boolean;
  @Input() isScheduledPayment: boolean;
  @Input() allowableCardBrands: AllowableCardTypes[];
  @Input() paymentExclusionOverrideSettings: PaymentExclusionOverrideSetting;
  @Input() hasCardOverridePermission: boolean;
  @Input() allowCreateSavedPaymentMethod: boolean;
  @Input() showPaymentToggleDefault: boolean;
  @Input() userCountry: string;
  @Input() isCanadianPadRequiredFlow: boolean;
  @Input() isCanadianPadOptionalFlow: boolean;
  @Input() supplierName: string;
  @Input() useQRCodeForRemoteCardCapture: string;
  @Input() sessionId: string;
  @Input() accountNumber: string;
  @Input() tenantId: string;
  @Input() authUserToken: string;
  @Input() disableSavePaymentAccount: boolean;
  @Input() showSignInNotice: boolean;
  @Input() showAutopaySuggestionBanner = false;

  @Output() cancelPaymentMethod = new EventEmitter();

  @Output() paymentMethodTypeToggled =
    new EventEmitter<PaymentMethodTypeToggledEvent>();

  @Output() submitAddPaymentMethodForm =
    new EventEmitter<AddPaymentMethodFormSubmitEvent>();

  @Output() submitAddPadAgreementForm =
    new EventEmitter<PadAgreementFormSubmitEvent>();

  @Output() paymentAccountCaptureSuccess = new EventEmitter<{
    paymentAccountData: PaymentAccountsPushNotificationMetadata;
    paymentMethod: AddPaymentMethodFormSubmitEvent;
    addPaymentMethodForm: AddPaymentMethodForm;
  }>();

  @Output() paymentAccountCaptureConnectionError = new EventEmitter<void>();

  displayAddPadAgreementDialog = false;
  displayModal = true;
  displayRemoteCardCaptureQRCode = signal(false);
  remoteCardCaptureSubscription: Subscription = null;
  private createWalletSessionSubscription: Subscription = null;
  isCreatingWalletSession = signal(false);
  walletSessionId = signal<string | null>(null);
  readonly AchType = 'ach';
  readonly CreditCardType = 'creditCard';

  padSignedSeparatelyConfirmationText: string;

  @ViewChild('padSignedSeparatelyConfirmation')
  padSignedSeparatelyConfirmation: ConfirmationComponent;

  @ViewChild('savePaymentMethodConfirmation')
  savePaymentMethodConfirmation: ConfirmationComponent;

  @ViewChild('paymentMethodForm', { static: true })
  paymentMethodFormComponent: PaymentMethodFormComponent;

  isSetIntervalPad = false;
  paymentMethod: AddPaymentMethodFormSubmitEvent;
  // card override
  cardOverrideEligible: boolean;
  usingCardOverride = false;

  createSavedPaymentAccountEnabled: boolean;
  paymentMethodType = PaymentMethodType;

  addPaymentForm: AddPaymentMethodForm = buildAddPaymentForm();

  get selectedPaymentMethodRef() {
    return this.addPaymentForm.controls.selectedPaymentMethod;
  }

  get selectedPaymentMethodType() {
    return this.selectedPaymentMethodRef.value;
  }

  private readonly walletSessionServiceFactory = inject(
    WalletSessionServiceFactory,
  );

  private readonly cdr = inject(ChangeDetectorRef);

  constructor(
    private translateService: TranslateService,
    private pushNotificationsService: PushNotificationsService,
  ) {}

  ngOnInit(): void {
    this.cardOverrideEligible =
      this.paymentExclusionOverrideSettings?.allowRestrictionOverride &&
      this.paymentExclusionOverrideSettings?.isBuyerAccountInRestrictionList &&
      this.hasCardOverridePermission;

    this.checkIsScheduledPayment();
    this.updateCreateSavedPaymentAccountEnabled();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.padSignedSeparatelyConfirmationText = this.translateService.instant(
      'ARC_PAYMENT_PAD_AGREEMENT_DIALOG_TEXT',
      {
        supplierName: this.supplierName,
      },
    ) as string;

    if (changes.isScheduledPayment) {
      this.checkIsScheduledPayment();
    }
  }

  onCancelPaymentMethod(): void {
    this.cancelPaymentMethod.emit();
  }

  onUpdatePaymentMethod(): void {
    if (!this.paymentMethodFormComponent.isFormValid()) {
      this.addPaymentForm.markAllAsTouched();
      this.addPaymentForm.updateValueAndValidity();
      return;
    }

    const paymentMethodDetails = getPaymentMethodFormDetails(
      this.addPaymentForm,
    );

    const savePaymentAccount = paymentMethodDetails.savePaymentAccount;

    this.paymentMethod = {
      valid: true,
      value: {
        ...paymentMethodDetails,
        cardOverrideUsed: this.usingCardOverride,
        padSignedSeparately: false,
      },
    };

    if (
      this.useQRCodeForRemoteCardCapture &&
      paymentMethodDetails.paymentMethodInformation.kind === this.CreditCardType
    ) {
      this.handleRemoteCardCaputre();
      return;
    }

    // Handle Canadian PAD flows for bank payments
    if (paymentMethodDetails.paymentMethodInformation?.kind === this.AchType) {
      if (this.isCanadianPadRequiredFlow) {
        this.displayModal = false;
        if (
          !savePaymentAccount &&
          !this.disableSavePaymentAccount &&
          this.allowCreateSavedPaymentMethod
        ) {
          this.savePaymentMethodConfirmation.confirm();
          return;
        }
        this.isSetIntervalPad = savePaymentAccount;
        this.displayAddPadAgreementDialog = true;
        return;
      }

      if (this.isCanadianPadOptionalFlow) {
        this.displayModal = false;
        this.padSignedSeparatelyConfirmation.confirm();
        return;
      }
    }

    this.submitAddPaymentMethodForm.emit(this.paymentMethod);
  }

  toggleChanged(): void {
    this.updateCreateSavedPaymentAccountEnabled();

    //if the option isn't displayed, then always disable the value
    if (!this.createSavedPaymentAccountEnabled) {
      this.addPaymentForm.controls.savePaymentAccount.setValue(false);
    }

    this.usingCardOverride = Boolean(
      this.cardOverrideEligible &&
        this.selectedPaymentMethodType === PaymentMethodType.CREDIT,
    );

    this.emitPaymentMethodTypeToggled();
  }

  emitPaymentMethodTypeToggled() {
    this.paymentMethodTypeToggled.emit({
      accountPaymentMethod:
        this.selectedPaymentMethodType == PaymentMethodType.ACH
          ? AccountPaymentMethod.Ach
          : AccountPaymentMethod.Card,
      cardOverrideUsed: this.usingCardOverride,
    });
  }

  checkIsScheduledPayment() {
    if (this.isScheduledPayment) {
      this.addPaymentForm.controls.savePaymentAccount.disable();
      this.addPaymentForm.controls.savePaymentAccount.setValue(true);
    } else {
      this.addPaymentForm.controls.savePaymentAccount.enable();
      this.addPaymentForm.controls.savePaymentAccount.setValue(false);
    }
  }

  onCardOverrideClick(): void {
    this.usingCardOverride = true;

    this.updateCreateSavedPaymentAccountEnabled();
    this.emitPaymentMethodTypeToggled();
    this.checkIsScheduledPayment();
  }

  onCancelPadSignedSeparatelyConfirmation() {
    this.displayModal = true;
  }

  onRejectPadSignedSeparatelyConfirmation() {
    this.displayModal = false;
    if (
      !this.paymentMethod.value.savePaymentAccount &&
      !this.disableSavePaymentAccount &&
      this.allowCreateSavedPaymentMethod
    ) {
      this.savePaymentMethodConfirmation.confirm();
    } else {
      this.isSetIntervalPad = this.paymentMethod.value.savePaymentAccount;
      this.displayAddPadAgreementDialog = true;
    }
  }

  onAcceptPadSignedSeparatelyConfirmation() {
    this.paymentMethod = {
      ...this.paymentMethod,
      value: {
        ...this.paymentMethod.value,
        padSignedSeparately: true,
      },
    };
    this.submitAddPaymentMethodForm.emit(this.paymentMethod);
  }

  onRejectSavePaymentMethodConfirm(): void {
    this.isSetIntervalPad = false;
    this.displayAddPadAgreementDialog = true;
  }

  onCancelSavePaymentMethodConfirm(): void {
    if (this.isCanadianPadRequiredFlow) {
      this.displayModal = true;
      return;
    }

    this.padSignedSeparatelyConfirmation.confirm();
  }

  onAcceptSavePaymentMethodConfirm(): void {
    this.isSetIntervalPad = true;
    this.displayAddPadAgreementDialog = true;
  }

  onCancelAddPadAgreement() {
    this.displayAddPadAgreementDialog = false;

    if (
      !this.paymentMethod.value.savePaymentAccount &&
      !this.disableSavePaymentAccount &&
      this.allowCreateSavedPaymentMethod
    ) {
      this.savePaymentMethodConfirmation.confirm();
    } else if (this.isCanadianPadOptionalFlow) {
      this.padSignedSeparatelyConfirmation.confirm();
    } else {
      this.displayModal = true;
    }
  }

  onSubmitAddPadAgreement(form: PadAgreementFormSubmitEvent) {
    this.paymentMethod = {
      ...this.paymentMethod,
      value: {
        ...this.paymentMethod.value,
        savePaymentAccount: this.isSetIntervalPad,
      },
    };
    this.submitAddPadAgreementForm.emit(form);
    this.submitAddPaymentMethodForm.emit(this.paymentMethod);
  }

  private updateCreateSavedPaymentAccountEnabled() {
    const paymentMethodType =
      this.addPaymentForm.controls.selectedPaymentMethod.value;

    // To save a payment method, first the setting in payment rules needs to be on.
    // Then, if it is ACH, the restrictions don't apply.
    // Otherwise, (we can infer it is a card), never show the option to save if the account is restricted, regardless of the ability to override.
    this.createSavedPaymentAccountEnabled =
      !this.disableSavePaymentAccount &&
      this.allowCreateSavedPaymentMethod &&
      (paymentMethodType === PaymentMethodType.ACH ||
        !this.paymentExclusionOverrideSettings
          ?.isBuyerAccountInRestrictionList);
  }

  ngOnDestroy(): void {
    this.createWalletSessionSubscription?.unsubscribe();
    this.remoteCardCaptureSubscription?.unsubscribe();
  }

  /* istanbul ignore next */
  private handleRemoteCardCaputre() {
    if (this.isCreatingWalletSession()) {
      return;
    }

    const formValues = this.addPaymentForm.getRawValue();
    const ccInfo = formValues.creditCard;
    const addressValues = formValues.billingInformation.address;

    const request = {
      sessionId: this.sessionId,
      ccName: ccInfo.name,
      saveAccount: formValues.savePaymentAccount ?? false,
      address1: addressValues.address1,
      address2: addressValues.address2,
      city: addressValues.city,
      state: addressValues.state,
      postalCode: addressValues.postalCode,
      country: addressValues.country,
    };

    this.isCreatingWalletSession.set(true);
    this.createWalletSessionSubscription = this.walletSessionServiceFactory
      .create()
      .createWalletSession(this.tenantId, request)
      .subscribe({
        next: (response) => {
          this.isCreatingWalletSession.set(false);
          this.walletSessionId.set(response.walletSessionId);
          this.displayRemoteCardCaptureQRCode.set(true);
          this.cdr.markForCheck();

          this.remoteCardCaptureSubscription =
            this.pushNotificationsService.subscribeToUserPaymentAccountsEvents(
              (notification) => {
                this.handleCloseQRCodeModal();
                this.paymentAccountCaptureSuccess.emit({
                  paymentAccountData: notification.event.metadata,
                  paymentMethod: this.paymentMethod,
                  addPaymentMethodForm: this.addPaymentForm,
                });
              },
              () => {
                this.handleCloseQRCodeModal();
                this.paymentAccountCaptureConnectionError.emit();
              },
            );
        },
        error: () => {
          this.isCreatingWalletSession.set(false);
          this.paymentAccountCaptureConnectionError.emit();
          this.cdr.markForCheck();
        },
      });
  }

  handleCloseQRCodeModal(): void {
    this.displayRemoteCardCaptureQRCode.set(false);
    if (this.remoteCardCaptureSubscription) {
      this.remoteCardCaptureSubscription?.unsubscribe();
      this.remoteCardCaptureSubscription = null;
    }
  }
}
